import { Menu } from '@mui/material';
import { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { editChartPropItem } from '../../../../redux/ChartPoperties/ChartPropertiesActions';
import {
  fieldName,
  findNewDisplayName,
  handleKeyDown,
} from '../../../CommonFunctions/CommonFunctions';
import WindowFunctionButtons from './WindowFunctionButtons';
import StandingButton from './StandingButton';
import SlidingButton from './SlidingButton';
import StandingVsSlidingButton from './StandingVsSlidingButton';
import StandingOptions from './StandingOptions';
import SlidingOptions from './SlidingOptions';
import { muiOutlinedInputStyle, radioButton } from '../ReusableStyles/CommonStyles';
import StandingSlidingOptions from './StandingSlidingOptions';
import WindowFunctionSelects from './WindowFunctionSelects';
import SlideDirectionSelector from './SlideDirectionSelector';
import { RootState } from '../../../../redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  callQueryAPI,
  updateExtraMeasureField,
} from '../../../../redux/ChartPoperties/ChartControlsActions';
import { setDisplayName } from '../../setDisplayName';
import { setPrefix } from '../../SetPrefix';

interface WindowFunctionProps {
  anchorElm: any;
  haswindowfn: boolean;
  setWindowfn: React.Dispatch<React.SetStateAction<boolean>>;
  propKey: string;
  bIndex: number;
  itemIndex: number;
  tooltipFormats?: any;
}

const WindowFunction = ({
  //props
  anchorElm,
  haswindowfn,
  setWindowfn,
  propKey,
  bIndex,
  itemIndex,
  tooltipFormats,
}: WindowFunctionProps) => {
  const { chartProp, tabTileProps, chartControls } = useSelector((state: RootState) => ({
    chartProp: state.chartProperties,
    tabTileProps: state.tabTileProps,
    chartControls: state.chartControls,
  }));

  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

  let chartType =
    chartProp.properties[`${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`].chartType;

  const extraMeasureField = tooltipFormats?.extraMeasureField || [];
  let field = tooltipFormats
    ? extraMeasureField[bIndex]?.fields[itemIndex]
    : chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex];

  let defaultWFObject = {
    windowFnOptions: 'standing',
    rank: 'Default',
    order: 'Descending',
    standingRowIndex: -1,
    standingColumnIndex: -1,
    standingRow: '(Entire Table)',
    standingColumn: '(Entire Table)',
    slidingPreInc: -999,
    slidingNextInc: 0,
    slidingCurrent: 1,
    slidingAggregation: 'Sum',
    slidingRowIndex: -1,
    slidingColumnIndex: -1,
    slidingRow: '(Entire Table)',
    slidingColumn: '(Entire Table)',
    slidingSlideDirection: 'rowwise',
    percentage: 'Percentage from',
    standingSlidingReferenceWn: null,
    standingSlidingPreInc: -1,
    standingSlidingNextInc: undefined,
    standingSlidingCurrent: 1,
    standingSlidingAggregation: 'Sum',
    standingSlidingRowIndex: -1,
    standingSlidingColumnIndex: -1,
    standingSlidingRow: '(Entire Table)',
    standingSlidingColumn: '(Entire Table)',
    standingSlidingSlideDirection: 'rowwise',
  };

  field.dataType = field.dataType.toLowerCase();

  const [windowFnValues, setWindowFnValues] = useState<any>(
    JSON.parse(JSON.stringify(defaultWFObject)),
  );

  const [selectedWindowFnOptions, setSelectedWindowFnOptions] = useState<string>('');

  type TWindowFnField = {
    fieldName: string;
    id: string;
  };

  //Fetch fields from Dimension, Row, Column, Distribution
  let rows: TWindowFnField[] = [];
  let columns: TWindowFnField[] = [];

  if (chartProp.properties[propKey].chartAxes[1].fields.length > 0) {
    chartProp.properties[propKey].chartAxes[1].fields.forEach((item: any) => {
      rows.push({
        fieldName: fieldName(item),
        id: item.uId,
      });
    });
  }

  if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
    if (chartProp.properties[propKey].chartAxes[2].fields.length > 0) {
      chartProp.properties[propKey].chartAxes[2].fields.forEach((item: any) => {
        columns.push({
          fieldName: fieldName(item),
          id: item.uId,
        });
      });
    }
  }

  const chartAxesTwoDim = chartProp.properties[propKey].chartAxes[2]
    ? chartProp.properties[propKey].chartAxes[2].fields
    : null;

  //If below condition get satisfied, then window function get disabled and its state values retain to its default values
  const chartAxesDimEmpty =
    (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType) &&
      chartProp.properties[propKey].chartAxes[1].fields.length === 0 &&
      chartProp.properties[propKey].chartAxes[2].fields.length === 0) ||
    (!['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richText'].includes(chartType) &&
      chartProp.properties[propKey].chartAxes[1].fields.length === 0);

  //Below condition get satisfied, then current(windowFnOption) in sliding and standingvssliding get checked and disabled
  const slidingCurrentDisable =
    (windowFnValues.slidingPreInc !== 0 && windowFnValues.slidingNextInc !== 0) ||
    (windowFnValues.slidingPreInc === 0 && windowFnValues.slidingNextInc === 0);
  const standingSlidingCurrentDisable =
    (windowFnValues.standingSlidingPreInc !== 0 && windowFnValues.standingSlidingNextInc !== 0) ||
    (windowFnValues.standingSlidingPreInc === 0 && windowFnValues.standingSlidingNextInc === 0);

  useEffect(() => {
    setSelectedWindowFnOptions(field.windowfn?.windowFnOptions);
  }, [field.windowfn]);

  useEffect(() => {
    setWindowFnValues(
      field.windowfn ? field.windowfn : JSON.parse(JSON.stringify(defaultWFObject)),
    );
  }, [haswindowfn]);

  useEffect(() => {
    if (rows.length !== 0 || columns.length !== 0) {
      const rowValues = ['(Entire Table)', ...rows.map((item) => item.id)];
      const columnValues = ['(Entire Table)', ...columns.map((item) => item.id)];
      const isStandingRowPresent = [windowFnValues.standingRow].some((value) =>
        rowValues.includes(value),
      );
      const isSlidingRowPresent = [windowFnValues.slidingRow].some((value) =>
        rowValues.includes(value),
      );
      const isStandingSlidingRowPresent = [windowFnValues.standingSlidingRow].some((value) =>
        rowValues.includes(value),
      );
      const isStandingColumnPresent = [windowFnValues.standingColumn].some((value) =>
        columnValues.includes(value),
      );
      const isSlidingColumnPresent = [windowFnValues.slidingColumn].some((value) =>
        columnValues.includes(value),
      );
      const isStandingSlidingColumnPresent = [windowFnValues.standingSlidingColumn].some((value) =>
        columnValues.includes(value),
      );

      if (
        isStandingRowPresent === false ||
        isSlidingRowPresent === false ||
        isStandingSlidingRowPresent === false ||
        isStandingColumnPresent === false ||
        isSlidingColumnPresent === false ||
        isStandingSlidingColumnPresent === false
      ) {
        setWindowFnValues((prevState: any) => ({
          ...prevState,
          ...defaultWFObject,
        }));
      }
    }

    if (chartAxesDimEmpty) {
      setWindowFnValues((prevState: any) => ({
        ...prevState,
        ...defaultWFObject,
      }));
    }

    if (slidingCurrentDisable) {
      setWindowFnValues((prevState: any) => ({
        ...prevState,
        slidingCurrent: 1,
      }));
    }

    if (standingSlidingCurrentDisable) {
      setWindowFnValues((prevState: any) => ({
        ...prevState,
        standingSlidingCurrent: 1,
      }));
    }

    //If mismatch of values between local and redux state of window function, then window function redux state value will be null and local state values retain to its default values
    if (field.windowfn) {
      if (rows.length !== 0 || columns.length !== 0) {
        const windowfnvalue = field.windowfn;
        const standingRowValue = [windowfnvalue.standingRow];
        const slidingRowValue = [windowfnvalue.slidingRow];
        const standingSlidingRowValue = [windowfnvalue.standingSlidingRow];
        const standingColumnValue = [windowfnvalue.standingColumn];
        const slidingColumnValue = [windowfnvalue.slidingColumn];
        const standingSlidingColumnValue = [windowfnvalue.standingSlidingColumn];
        const rowValues = ['(Entire Table)', ...rows.map((item) => item.id)];
        const columnValues = ['(Entire Table)', ...columns.map((item) => item.id)];
        const isStandingRowPresentInRows = standingRowValue.some((value) =>
          rowValues.includes(value),
        );
        const isSlidingRowPresentInRows = slidingRowValue.some((value) =>
          rowValues.includes(value),
        );
        const isStandingSlidingRowPresentInRows = standingSlidingRowValue.some((value) =>
          rowValues.includes(value),
        );
        const isStandingColumnsPresentInColumns = standingColumnValue.some((value) =>
          columnValues.includes(value),
        );
        const isSlidingColumnsPresentInColumns = slidingColumnValue.some((value) =>
          columnValues.includes(value),
        );
        const isStandingSlidingColumnsPresentInColumns = standingSlidingColumnValue.some((value) =>
          columnValues.includes(value),
        );

        if (
          isStandingRowPresentInRows === false ||
          isSlidingRowPresentInRows === false ||
          isStandingSlidingRowPresentInRows === false ||
          isStandingColumnsPresentInColumns === false ||
          isSlidingColumnsPresentInColumns === false ||
          isStandingSlidingColumnsPresentInColumns === false
        ) {
          const field2 = { ...field };
          field2.windowfn = null;
          if (tooltipFormats) {
            dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, field2));
          } else {
            dispatch(
              editChartPropItem('updateQuery', {
                propKey,
                binIndex: bIndex,
                itemIndex,
                item: field2,
              }),
            );
          }

          setWindowFnValues((prevState: any) => ({
            ...prevState,
            ...defaultWFObject,
          }));
        }
      }
    }
  }, [
    chartProp.properties[propKey].chartAxes[1].fields,
    chartAxesTwoDim,
    chartAxesDimEmpty,
    slidingCurrentDisable,
    standingSlidingCurrentDisable,
  ]);

  const textFieldStyleProps = {
    style: {
      fontSize: '12px',
      width: '58px',
      backgroundColor: 'white',
      height: '26px',
    },
  };

  const ranks: string[] = ['Default', 'Dense', 'Unique'];
  const orders: string[] = ['Ascending', 'Descending'];
  const percentages: string[] = [
    'Percentage from',
    'Difference from',
    'Percentage difference from',
  ];
  const slidingAggregation: any[] = ['Sum', 'Avg', 'Min', 'Max', 'Count'];
  const standingSlidingAggregation: any[] = ['Sum', 'Avg', 'Min', 'Max'];

  return (
    <div>
      <Menu
        anchorEl={anchorElm}
        open={haswindowfn}
        onClose={() => setWindowfn(false)}
        sx={{
          marginRight: tooltipFormats ? '150px' : undefined,
          marginLeft: tooltipFormats ? undefined : '180px',
        '.MuiPaper-root': {
          width: '222px',
          maxHeight: 'none',
          overflow: 'visible',
        },
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            sx: {
              paddingBottom: '0px',
            },
          },
          paper: tooltipFormats
            ? {
                sx: {
                  top: '250px !important',
                  right: '85px !important',
                  left: 'initial !important',
                },
              }
            : undefined,
        }}
      >
        <StandingButton
          isSelected={windowFnValues.windowFnOptions === 'standing'}
          isSelectedFn={selectedWindowFnOptions === 'standing'}
          onClick={() => {
            setWindowFnValues((prevState: any) => ({
              ...prevState,
              windowFnOptions: 'standing',
            }));
          }}
        />

        <SlidingButton
          isSelected={windowFnValues.windowFnOptions === 'sliding'}
          isSelectedFn={selectedWindowFnOptions === 'sliding'}
          onClick={() => {
            setWindowFnValues((prevState: any) => ({
              ...prevState,
              windowFnOptions: 'sliding',
            }));
          }}
        />

        <StandingVsSlidingButton
          isSelected={windowFnValues.windowFnOptions === 'standingsvssliding'}
          isSelectedFn={selectedWindowFnOptions === 'standingsvssliding'}
          onClick={() => {
            setWindowFnValues((prevState: any) => ({
              ...prevState,
              windowFnOptions: 'standingsvssliding',
            }));
          }}
        />

        {windowFnValues.windowFnOptions === 'standing' ? (
          <StandingOptions
            windowFnValues={windowFnValues}
            setWindowFnValues={setWindowFnValues}
            ranks={ranks}
            orders={orders}
            radioButton={radioButton}
          />
        ) : null}

        {windowFnValues.windowFnOptions === 'sliding' ? (
          <SlidingOptions
            windowFnValues={windowFnValues}
            setWindowFnValues={setWindowFnValues}
            slidingAggregation={slidingAggregation}
            textFieldStyleProps={textFieldStyleProps}
            muiOutlinedInputStyle={muiOutlinedInputStyle}
            radioButton={radioButton}
            handleKeyDown={handleKeyDown}
          />
        ) : null}

        {windowFnValues.windowFnOptions === 'standingsvssliding' ? (
          <StandingSlidingOptions
            windowFnValues={windowFnValues}
            setWindowFnValues={setWindowFnValues}
            percentages={percentages}
            standingSlidingAggregation={standingSlidingAggregation}
            handleKeyDown={handleKeyDown}
          />
        ) : null}

        <WindowFunctionSelects
          chartType={chartType}
          rows={rows}
          columns={columns}
          windowFnValues={windowFnValues}
          setWindowFnValues={setWindowFnValues}
          propKey={propKey}
          chartProp={chartProp}
        />

        {(windowFnValues.windowFnOptions === 'standingsvssliding' &&
          ['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) ||
        (windowFnValues.windowFnOptions === 'sliding' &&
          ['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) ? (
          <SlideDirectionSelector
            windowFnValues={windowFnValues}
            setWindowFnValues={setWindowFnValues}
            chartProp={chartProp}
            propKey={propKey}
          />
        ) : null}

        <WindowFunctionButtons
          onRemove={() => {
            setWindowfn(false);
            let field2 = JSON.parse(JSON.stringify(field));
            field2.windowfn = null;
            field2.displayname = fieldName(field2); // for getting name without window fn
            if (tooltipFormats) {
              field2.displayname = findNewDisplayName(
                chartProp.properties[propKey].chartAxes,
                field2,
                tooltipFormats,
              );
              dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, field2));
            } else {
              field2.displayname = findNewDisplayName(
                chartProp.properties[propKey].chartAxes,
                field2,
              );
              dispatch(
                editChartPropItem('updateQuery', {
                  propKey,
                  binIndex: bIndex,
                  itemIndex,
                  item: field2,
                }),
              );
            }
           if (field.windowfn) dispatch(callQueryAPI(propKey, true));
          }}
          onSave={() => {
            setWindowfn(false);
            let field2 = JSON.parse(JSON.stringify(field));
            let modifyingWinFn = false;
            if (field.windowfn) modifyingWinFn = true;
            field2.windowfn = windowFnValues;

            const axisName = chartProp.properties[propKey].chartAxes[bIndex].name;
            let newFieldData = field2;
            if (!modifyingWinFn) {
              newFieldData = setDisplayName(
                setPrefix(field2, axisName, chartType),
                axisName,
                chartType,
              );
              newFieldData.displayname = newFieldData.displayname.startsWith('window fn. of')
                ? newFieldData.displayname
                : `window fn. of ${newFieldData.displayname}`;
              newFieldData.displayname = findNewDisplayName(
                chartProp.properties[propKey].chartAxes,
                newFieldData,
                chartControls.properties?.[propKey]?.formatOptions?.tooltipFormats,
              );
            }
            if (tooltipFormats) {
              dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, newFieldData));
            } else {
              dispatch(
                editChartPropItem('updateQuery', {
                  propKey,
                  binIndex: bIndex,
                  itemIndex,
                  item: newFieldData,
                }),
              );
            }
            dispatch(callQueryAPI(propKey, true));
          }}
        />
      </Menu>
    </div>
  );
};

export default WindowFunction;
